import { useMutation } from '@apollo/client/react';

import type { Event } from '../../Data/types';
import { INSERT_EVENT, GET_FILTERED_EVENTS } from '../../graphql/queries';
import { safeToNumber } from '../../helpers/safeNumberConversion/safeNumberConversion';
import useBuildGraphQLVariables from '../useBuildGraphQLVariables/useBuildGraphQLVariables';

function useCreateEvent() {
  const filters = useBuildGraphQLVariables(); // Retrieve current filters for refetching
  const [createEventMutation, { loading, error }] = useMutation(INSERT_EVENT, {
    refetchQueries: [
      {
        query: GET_FILTERED_EVENTS,
        variables: { filters: filters },
      },
    ],
  });

  // return a function that you can call with the necessary arguments to create an event
  const createEvent = (event: Event, currentUser: string) => {
    return createEventMutation({
      variables: {
        event: {
          audience_in_events: {
            data: event.audience
              .map((audience) => safeToNumber(audience.id))
              .filter((id): id is number => id !== null)
              .map((audienceId) => ({ audience_id: audienceId })),
          },
          duration: event.recurrence?.duration,
          end_date_time: event.isRecurring
            ? event.recurrence!.endDate?.format('YYYY-MM-DDTHH:mm:ss')
            : event.eventDateTime?.end?.format('YYYY-MM-DDTHH:mm:ss'),
          end_date_time_utc: event.isRecurring ? event.recurrence!.endDate?.utc() : event.eventDateTime?.end?.utc(),
          contact_in_events:
            event.eventContact && event.eventContact.length > 0
              ? {
                  data: event.eventContact.map((contact) => ({
                    user_id: contact.ADSystemID,
                  })),
                }
              : undefined,
          event_details: event.description,
          event_type_id: event.eventType?.id || 5, // Default to 5 if eventType is not provided
          event_type_other: event.eventTypeOther,
          external_office: event.externalLocation,
          hosting_group_other: event.hostingGroupOther,
          is_all_day: event.allDay,
          is_client_event_or_celebration: event.clientEventOrCelebration,
          is_cle: event.cle,
          hosting_type: event.hostingType,
          is_invite_only: event.inviteOnly,
          is_recurring: event.isRecurring,
          event_format_id: event.eventFormat?.id || null,
          speaker_in_events:
            event.mlSpeaker && event.mlSpeaker.length > 0
              ? {
                  data: event.mlSpeaker.map((speaker) => ({
                    user_id: speaker.ADSystemID,
                  })),
                }
              : undefined,
          offices_in_events:
            event.officeLocation && event.officeLocation.length > 0
              ? {
                  data: event.officeLocation.map((office) => ({
                    office_id: office.id,
                  })),
                }
              : undefined,
          office_visibility_in_events:
            event.officeVisibility && event.officeVisibility.length > 0
              ? {
                  data: event.officeVisibility.map((office) => ({
                    office_id: office.id,
                  })),
                }
              : undefined,
          outside_speakers: event.outsideSpeakers
            ? {
                data: event.outsideSpeakers.map((speaker) => ({
                  name: speaker.name,
                  organization: speaker.organization,
                  title: speaker.title,
                })),
              }
            : undefined,
          recurrence_pattern: event.recurrence?.recurrencePattern,
          sponsoring_orgnization: event.sponsorOrganization,
          start_date_time: event.isRecurring
            ? event.recurrence!.startDate?.format('YYYY-MM-DDTHH:mm:ss')
            : event.eventDateTime?.start?.format('YYYY-MM-DDTHH:mm:ss'),
          start_date_time_utc: event.isRecurring
            ? event.recurrence!.startDate?.utc()
            : event.eventDateTime?.start?.utc(),
          sponsoring_attorney_in_events:
            event.sponsoringAttorney && event.sponsoringAttorney.length > 0
              ? {
                  data: event.sponsoringAttorney.map((attorney) => ({
                    user_id: attorney.ADSystemID,
                  })),
                }
              : undefined,
          time_zone: event.timezone,
          title: event.title,
          firm_initiatives_in_event: {
            data: event.firmInitiatives
              .map((initiative) => safeToNumber(initiative.id))
              .filter((id): id is number => id !== null)
              .map((initiativeId) => ({ firm_initiative_id: initiativeId })),
          },
          industries_in_event: {
            data: event.industries
              .map((industry) => safeToNumber(industry.id))
              .filter((id): id is number => id !== null)
              .map((industryId) => ({ industry_id: industryId })),
          },
          departments_in_event: {
            data: event.departments
              .map((department) => safeToNumber(department.id))
              .filter((id): id is number => id !== null)
              .map((departmentId) => ({ department_id: departmentId })),
          },
          practice_groups_in_event: {
            data: event.practiceGroups
              .map((practiceGroup) => safeToNumber(practiceGroup.id))
              .filter((id): id is number => id !== null)
              .map((practiceGroupId) => ({ practice_group_id: practiceGroupId })),
          },
          created_by: currentUser,
          updated_by: currentUser,
        },
      },
    });
  };

  return { createEvent, loading, error };
}

export default useCreateEvent;
